import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * MagneticButton — cursor-attracted brass CTA.
 *
 * Pure CSS-driven feel via Framer Motion springs. The button translates a
 * fraction of the cursor's offset from its center, then snaps back on leave.
 * Use exactly like a <button>. Forwards refs so <form> submit still works.
 */
export const MagneticButton = React.forwardRef(function MagneticButton(
  { children, strength = 0.25, className = '', style, ...rest },
  forwardedRef
) {
  const localRef = useRef(null);
  const ref = forwardedRef || localRef;
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseMove = (e) => {
    const node = (ref.current ||= e.currentTarget);
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const x = (e.clientX - (rect.left + rect.width / 2)) * strength;
    const y = (e.clientY - (rect.top + rect.height / 2)) * strength;
    setPos({ x, y });
  };

  const onMouseLeave = () => setPos({ x: 0, y: 0 });

  return (
    <motion.button
      ref={ref}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      animate={{ x: pos.x, y: pos.y }}
      transition={{ type: 'spring', stiffness: 220, damping: 18, mass: 0.4 }}
      className={className}
      style={style}
      {...rest}
    >
      {children}
    </motion.button>
  );
});

export default MagneticButton;
